import type { CSSProperties } from "react";
import { cn } from "./cn";

/**
 * ŞİFRE GÜCÜ ÇUBUĞU — `SifreSifirlama.dc.html`
 *
 * ================================================================
 * BU BİLEŞEN KARAR VERMİYOR, GÖSTERİYOR
 * ================================================================
 * Seviyeyi ÇAĞIRAN taraf ölçüyor (`jeton-sayfasi.ts` → `sifreGucu`),
 * kabul kararını ise SUNUCU veriyor (`password.ts`). Burada yalnızca
 * çizim var. Ölçütü buraya taşımak, `packages/ui`ya bir güvenlik
 * kuralı gömmek olurdu — ve o kural sunucudakinden ayrı düşerdi.
 *
 * ================================================================
 * SIFIR SEVİYE HİÇ ÇİZİLMİYOR — VE BUNU BİLEŞEN GARANTİ EDİYOR
 * ================================================================
 * `sifreGucu("")` sıfır döner ve bu "zayıf" DEĞİL, "ölçülmedi"
 * demek. Boş bir alanın altında dört gri çubuk çizmek, kullanıcıya
 * daha tek harf yazmadan bir yargı bildirirdi.
 *
 * Kontrol çağıran tarafta değil BURADA: çağıran her yerde `seviye > 0`
 * yazmayı unutmak mümkün ve unutulduğunda hiçbir şey bağırmaz
 * (CLAUDE.md kural 10).
 *
 * ================================================================
 * ANİMASYON SEVİYE DEĞİŞİNCE TEKRAR OYNUYOR
 * ================================================================
 * `key` seviyeyi taşıyor. Taşımasaydı CSS animasyonu yalnızca ilk
 * çizimde bir kez oynardı — `tokens.css` içindeki "her tuşa basışta
 * tekrarlanıyor" cümlesi yalan olurdu. Çubuğun büyüyerek gelmesi
 * değişimin YÖNÜNÜ söylüyor; anlık sıçrayan bir çubuk "hata mı
 * yaptım" hissi veriyor.
 */

export type GucSeviyesi = 0 | 1 | 2 | 3 | 4;

/**
 * Dolu çubuğun ve metnin rengi.
 *
 * Çizim yalnızca "güçlü" karesini veriyor (`onay-600` çubuk,
 * `onay-700` metin); alt seviyelerin rengi ondan türetildi ve
 * sistemin kendi anlam renklerinden seçildi. Metin tonları açık
 * zeminde AA'yı geçen koyu uçlar: `hata-700` 6,67 · `uyari-800` ·
 * `onay-700` 6,30.
 *
 * RENK TEK BAŞINA ANLAM TAŞIMIYOR: seviyenin adı her zaman yazıyla
 * da yazılıyor ve dolu çubuk SAYISI da aynı bilgiyi veriyor.
 */
const TON: Record<Exclude<GucSeviyesi, 0>, { cubuk: string; metin: string }> = {
  1: { cubuk: "bg-hata-500", metin: "text-hata-700" },
  2: { cubuk: "bg-uyari-600", metin: "text-uyari-800" },
  3: { cubuk: "bg-onay-600", metin: "text-onay-700" },
  4: { cubuk: "bg-onay-600", metin: "text-onay-700" },
};

const CUBUK_SAYISI = 4;

export interface SifreGucuProps {
  seviye: GucSeviyesi;
  /**
   * Seviyenin adı — "Zayıf" · "Orta" · "İyi" · "Güçlü".
   *
   * Dışarıdan geliyor çünkü adlar ölçütün bir parçası ve ölçüt
   * çağıran tarafta (`GUC_ADI`). İki yerde tutmak, ölçüt
   * değiştiğinde adların eski kalmasına yol açardı.
   */
  ad: string;
  className?: string;
}

export function SifreGucu({ seviye, ad, className }: SifreGucuProps) {
  if (seviye === 0) return null;

  const ton = TON[seviye];

  return (
    <div className={cn("flex flex-col gap-1.5 pt-0.5", className)}>
      {/*
        Ölçüler çizimden: dört çubuk, 4px yükseklik, 5px aralık,
        tam yuvarlak uçlar. Çubuklar `aria-hidden` — aynı bilgiyi
        altındaki metin zaten okunabilir biçimde söylüyor ve dört
        `div`i ekran okuyucuya duyurmak gürültüden başka bir şey
        üretmezdi.
      */}
      <div aria-hidden="true" className="flex gap-[5px]">
        {Array.from({ length: CUBUK_SAYISI }, (_, i) => (
          <span
            key={`${seviye}-${i}`}
            className={cn("guclen h-1 flex-1 rounded-full", i < seviye ? ton.cubuk : "bg-notr-200")}
            // Çizimde gecikme 70ms artıyor: çubuklar aynı anda değil
            // SIRAYLA doluyor ve sıra bilgisi animasyonun kendisinde.
            style={{ animationDelay: `${i * 70}ms` } satisfies CSSProperties}
          />
        ))}
      </div>

      {/*
        `aria-live="polite"`: kullanıcı yazarken seviye değişiyor ve
        ekran okuyucu kullanıcısının bunu öğrenmesinin başka yolu yok.
        `polite` — yazmayı kesmiyor, ilk boşlukta söylüyor.
      */}
      <span aria-live="polite" className={cn("text-[12.5px]", ton.metin)}>
        Şifre gücü: {ad}
      </span>
    </div>
  );
}
